import React from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import { LiveBlogAdController } from 'lib/AdControllers';
import { isBlogEventEnded, isBlogLive, shouldRenderRegularBlog } from 'lib/liveBlog';

import Ad from 'components/Ad';
import Card from 'components/Card';
import Header from '../Header';
import Pill from '../Pill';

import styles from './styles.module.scss';

const {
  LIVE_BLOG_POLLING_INTERVAL = 30000,
} = process.env;

const mapStateToProps = ({ article, liveBlog }) => ({
  flag: get(article, 'content[0].flag', null),
  pagination: liveBlog.pagination,
  activeItems: liveBlog.activeItems,
  canonicalUrl: get(article, 'content[0].url.canonical', null),
  isRegularBlog: shouldRenderRegularBlog(get(article, 'content[0].subType'), null),
});

@connect(mapStateToProps)
class AMP extends React.Component {
  static propTypes = {
    flag: PropTypes.string,
    pagination: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        page: PropTypes.number,
        size: PropTypes.number,
        totalItems: PropTypes.number,
        totalPages: PropTypes.number,
      }),
    ]),
    activeItems: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
    canonicalUrl: PropTypes.string.isRequired,
    isRegularBlog: PropTypes.bool,
  };

  static defaultProps = {
    flag: null,
    pagination: false,
    activeItems: [],
    isRegularBlog: false,
  };

  link = (page, label = false, cls = false) => <a href={`?page=${page}#live-blog`} className={cls}>{label || page}</a>;

  pagination = () => {
    const { pagination: { page, totalPages } = {} } = this.props;

    if (!page) {
      return null;
    }

    const pages = [];
    let prev = (
      <span className={styles.disabled}>{ i18next.t('Prev') }</span>
    );
    let next = (
      <span className={styles.disabled}>{ i18next.t('Next') }</span>
    );

    if (page > 1) {
      // Previuos page links
      for (let p = 1; p < 4; p += 1) {
        pages.unshift(
          <li>{page - p >= 1 ? this.link(page - p) : '\u00A0'}</li>,
        );
      }
      // ellipsis
      if (page - 4 >= 1) {
        pages.unshift(<li>{'\u2026'}</li>);
      }
      // Previous page link
      prev = this.link(page - 1, i18next.t('Prev'), styles.button);
    }

    // Current page
    pages.push(
      <li><span className={styles.active}>{page}</span></li>,
    );

    if (page < totalPages) {
      // Next page links
      for (let n = 1; n < 4; n += 1) {
        pages.push(
          <li>{page + n <= totalPages ? this.link(page + n) : '\u00A0'}</li>,
        );
      }
      // ellipsis
      if (page + 4 < totalPages) {
        pages.push(<li>{'\u2026'}</li>);
      }
      // Next page link
      next = this.link(page + 1, i18next.t('Next'), styles.button);
    }

    // Prev/next link or label
    pages.unshift(<li>{prev}</li>);
    pages.push(<li>{next}</li>);

    return (
      /* Empty string attribute to prevent React from removing */
      <div pagination="" className="mv8">
        <nav aria-label="amp live list pagination">
          <ul className={styles.pagination}>
            {pages}
          </ul>
        </nav>
      </div>
    );
  }

  /**
   * Render the amp-live-list markup.
   * @see https://github.com/ampproject/amphtml/blob/master/extensions/amp-live-list/amp-live-list.md
   */
  render() {
    const {
      flag,
      pagination: { page },
      activeItems,
      canonicalUrl,
      isRegularBlog,
    } = this.props;

    const liveListAttributes = {
      id: 'live-blog',
      'data-poll-interval': LIVE_BLOG_POLLING_INTERVAL,
      'data-max-items-per-page': 10,
    };

    // Disable AMP update polling on pages other than first
    if (page > 1) {
      liveListAttributes.disabled = '';
    }

    const cardsWithAds = LiveBlogAdController.insertBoxinlineAds(activeItems);

    const isLive = !isBlogEventEnded(flag)
      && isBlogLive(get(activeItems, '[0].date.publishedAt', null));

    return (
      <React.Fragment>
        {isRegularBlog ? <div className="mb8" /> : <Header isLive={isLive} />}
        <amp-live-list {...liveListAttributes}>
          <Pill
            isAmp
            attributes={{
              update: '',
              on: `tap:${liveListAttributes.id}.update`,
            }}
          />

          {/* Empty string attribute to prevent React from removing */}
          <div items="" className="clearfix">
            {cardsWithAds.map((cardData) => {
              if (cardData.type === 'ad') {
                return (
                  <Ad
                    {...cardData}
                    id={cardData.key}
                    adClass="mb8"
                    liveBlogItem
                  />
                );
              }

              return (
                <Card
                  {...cardData}
                  path={canonicalUrl}
                  key={cardData.id}
                  canonicalUrl={canonicalUrl}
                />
              );
            })}
          </div>

          {this.pagination()}
        </amp-live-list>
      </React.Fragment>
    );
  }
}

export default AMP;
